import { performApiLogout } from '@libs/common-next';
import {
    evaluateSession,
    getPathnameFromPath,
    isRestrictedPathname,
    isUserLoggedIn,
    redirectToSignIn,
    setPendingRedirectDestination,
} from '@libs/core';

interface HandleAccountClickProps {
    event?: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>;
    href?: string;
    link?: string;
    skipRedirect?: boolean;
    skipNavigation?: boolean;
    router?: { push: (href: string) => void };
}

function resolveLink(link?: string, href?: string): string | undefined {
    if (link) {
        return link;
    }
    if (href == null) {
        return undefined;
    }
    if (globalThis.window === undefined) {
        return href;
    }
    return `${globalThis.window.location.origin}${href}`;
}

function extractDestinationPath(link: string): string {
    try {
        const url = new URL(link, globalThis.location.origin);
        return url.pathname + url.search + url.hash;
    } catch {
        return link;
    }
}

function extractRouterPath(link: string): string {
    try {
        const url = new URL(link);
        return url.pathname + url.search + url.hash;
    } catch {
        return link;
    }
}

async function handleRestrictedPathNavigation(link: string, skipRedirect?: boolean): Promise<void> {
    const destinationPath = extractDestinationPath(link);
    setPendingRedirectDestination(destinationPath);
    await evaluateSession({ skipRedirect, redirectUrl: destinationPath, performApiLogout });

    if (skipRedirect) {
        setPendingRedirectDestination(null);
    }
}

function navigateToLink(link: string, router?: { push: (href: string) => void }): void {
    if (!router) {
        globalThis.location.assign(link);
        return;
    }

    if (link.startsWith('http://') || link.startsWith('https://')) {
        router.push(extractRouterPath(link));
    } else {
        router.push(link);
    }
}

/**
 * uses next router for client-side navigation.  Preserves import logic
 * (ie restricted path checking, conditional evaluateSession)
 *
 * @param router next.js router used for client-side nav, otherwise use full page refresh
 */
export async function handleAccountClick({
    event,
    href,
    link,
    skipRedirect,
    skipNavigation,
    router,
}: Readonly<HandleAccountClickProps>) {
    const resolvedLink = resolveLink(link, href);
    if (!resolvedLink) {
        return;
    }
    event?.preventDefault();

    const isGoingToRestrictedPath = isRestrictedPathname(getPathnameFromPath(resolvedLink));
    const wasLoggedIn = isUserLoggedIn();

    if (isGoingToRestrictedPath) {
        await handleRestrictedPathNavigation(resolvedLink, skipRedirect);
    }

    if (!skipNavigation) {
        if (!isGoingToRestrictedPath) {
            setPendingRedirectDestination(null);
            navigateToLink(resolvedLink, router);
            return;
        }

        // Cookie-less visits to a protected URL bounce to `/sign-in` with no destination.
        // If evaluateSession just logged the user out, that path already started the
        // sign-in redirect. If they were already logged out, send them there now.
        if (!skipRedirect && !isUserLoggedIn()) {
            if (!wasLoggedIn) {
                redirectToSignIn({ destination: extractDestinationPath(resolvedLink) });
            }
            return;
        }

        navigateToLink(resolvedLink, router);
        return;
    }

    if (router && isUserLoggedIn()) {
        navigateToLink(resolvedLink, router);
    }
}
